import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import analyticsService from "../services/analyticsService";
import { buildStationPanel } from "../utils/stationPanel";
import ScreenLayout from "./ui/ScreenLayout";
import LanguageSwitcher from "./ui/LanguageSwitcher";
import Icon from "./ui/Icon";

const ChoiceCard = ({ primary = false, icon, title, text, disabled = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`w-full min-h-[88px] flex items-center gap-4 rounded-2xl p-5 text-left transition ${
      primary
        ? "bg-qv-brand text-white shadow-qv-cta hover:bg-qv-brand-hover"
        : "bg-white text-qv-ink border-[1.5px] border-qv-brand hover:bg-qv-brand-50"
    } disabled:opacity-50 disabled:cursor-not-allowed`}
  >
    <span
      className={`h-10 w-10 shrink-0 md:h-11 md:w-11 rounded-full flex items-center justify-center ${
        primary ? "bg-white/15" : "bg-qv-brand-50"
      }`}
    >
      <Icon name={icon} size={24} className={primary ? "text-white" : "text-qv-brand"} />
    </span>
    <span className="flex-1 flex flex-col gap-0.5">
      <span className="text-[17px] font-bold">{title}</span>
      <span className={`text-[13px] leading-[19px] font-medium ${primary ? "text-qv-brand-100" : "text-qv-muted"}`}>
        {text}
      </span>
    </span>
    <Icon name="alt-arrow-right-linear" size={20} className={primary ? "text-white" : "text-qv-brand"} />
  </button>
);

/**
 * Écran 4a, affiché au scan du QR code lorsqu'une recharge est déjà en cours sur
 * la borne. Le client choisit explicitement son intention au lieu d'être envoyé
 * d'office sur la vérification CB :
 *   - nouvelle recharge  → écran d'arrivée (prise libre seule, 4b), puis paiement
 *   - reprise de session → saisie des 4 derniers chiffres de la carte (2b)
 */
export default function SessionChoiceComponent() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { evcpData, eligibilityData, reference, partner } = location.state || {};

  const hasRequiredData = Boolean(evcpData?.connectors);

  useEffect(() => {
    if (!hasRequiredData) {
      navigate("/error", {
        state: { errorText: t("noBorneData") },
      });
      return;
    }

    analyticsService.pushPageView("session_choice", {
      station_id: evcpData.reference,
    });
  }, [hasRequiredData, evcpData, navigate, t]);

  if (!hasRequiredData) {
    return null;
  }

  const connectors = evcpData.connectors.filter(
    (connector) => connector.type !== "DOMESTIC_E"
  );
  const busyConnectors = connectors.filter((connector) => connector.hasTransaction);
  const freeConnectors = connectors.filter(
    (connector) =>
      !connector.hasTransaction &&
      (connector.status === "Available" || connector.status === "Preparing")
  );

  const canStartNewCharge = freeConnectors.length > 0;

  const handleStartNewCharge = () => {
    if (!canStartNewCharge) return;

    analyticsService.pushEvent("intent_selected", {
      station_id: evcpData.reference,
      intent: "new",
    });

    navigate(`/connectors/${reference}`, {
      state: { evcpData, eligibilityData, reference, partner, mode: "start" },
    });
  };

  const handleResumeSession = () => {
    analyticsService.pushEvent("intent_selected", {
      station_id: evcpData.reference,
      intent: "resume",
    });

    // Une seule recharge en cours : pas d'ambiguïté sur le connecteur à reprendre.
    if (busyConnectors.length === 1) {
      navigate("/resume-session", {
        state: { evcpData, connector: busyConnectors[0], reference, partner },
      });
      return;
    }

    navigate(`/connectors/${reference}`, {
      state: { evcpData, eligibilityData, reference, partner, mode: "resume" },
    });
  };

  return (
    <ScreenLayout
      headerRight={<LanguageSwitcher />}
      panel={buildStationPanel({ t, language: i18n.language, evcpData, connectors, withStatus: true })}
    >
      <h1 className="mb-2 text-[28px] leading-9 tracking-[-.02em] font-bold md:text-[32px] md:leading-10">{t("intent.title")}</h1>
      <p className="mb-7 text-[15px] leading-6 text-qv-muted">{t("intent.subtitle")}</p>

      <div className="flex flex-col gap-4">
        <ChoiceCard
          primary
          icon="plug-circle-linear"
          title={t("intent.startTitle")}
          text={canStartNewCharge ? t("intent.startText") : t("sessionChoice.startNew.noConnectorAvailable")}
          disabled={!canStartNewCharge}
          onClick={handleStartNewCharge}
        />
        <ChoiceCard
          icon="history-linear"
          title={t("intent.resumeTitle")}
          text={t("intent.resumeText")}
          onClick={handleResumeSession}
        />
      </div>
    </ScreenLayout>
  );
}
